import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Languages, Project } from "types";
import { DocType } from "../types";
import { Modal } from "components/Modal/Modal";
import { Feature, useFeatureFlags } from "context/FeatureFlag";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { DocumentsGenerationLoader } from "./DocumentsGenerationLoader";
import { GeneratePDFParams } from "pages/Details/hooks/useProjectDetails";
import { flagsCode } from "components/common/LanguageSelector";
// import { JsonDiff } from "./JsonDiff";
// import { useProjectVersionDiff } from "./hooks/useProjectVersionDiff";

interface GenerateDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (params: GeneratePDFParams) => void;
  selectedDocs: DocType[];
  selectedLanguages: string[];
  projectName?: string;
  project?: Project;
  previousRevisionKey?: string;
}

export const GenerateDocumentsModal: React.FC<GenerateDocumentsModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  selectedDocs,
  selectedLanguages,
  projectName,
  // project,
  // previousRevisionKey,
}) => {
  const { t } = useTranslation();
  const { isFeatureEnabled } = useFeatureFlags();
  const [comment, setComment] = useState<string>("");
  const [isDryRun, setIsDryRun] = useState<boolean>(true);
  const [revisionName, setRevisionName] = useState<string>("");
  const [revisionNameError, setRevisionNameError] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [availableLanguages, setAvailableLanguages] = useState<Languages[]>([]);
  const [userSelectedLanguages, setUserSelectedLanguages] = useState<string[]>(
    []
  );

  // // Get version diff data
  // const {
  //   currentProject,
  //   previousSnapshot,
  //   changedKeys,
  //   isLoading: isDiffLoading,
  //   error: diffError,
  // } = useProjectVersionDiff(project, previousRevisionKey);

  // Initialize available languages and selected languages
  useEffect(() => {
    if (isOpen && !isGenerating) {
      setAvailableLanguages(Object.values(Languages));
      setUserSelectedLanguages(selectedLanguages);
    }
  }, [isOpen, selectedLanguages, isGenerating]);

  const handleLanguageToggle = (langCode: string) => {
    setUserSelectedLanguages((prev) =>
      prev.includes(langCode)
        ? prev.filter((lang) => lang !== langCode)
        : [...prev, langCode]
    );
  };

  const handleSubmit = () => {
    setIsGenerating(true);
    onGenerate({
      selectedDocs,
      selectedLanguages: userSelectedLanguages,
      revisionName,
      comment,
      isDryRun,
      isUserGenerated: true,
      callback: handleGenerationComplete,
    });
  };

  const handleGenerationComplete = () => {
    setIsGenerating(false);
    onClose();
  };

  const modalContent = isGenerating ? (
    <DocumentsGenerationLoader
      selectedDocs={selectedDocs}
      selectedLanguages={userSelectedLanguages}
      onComplete={handleGenerationComplete}
    />
  ) : (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          {t("documents.generate.title")}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t("documents.generate.description")}
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <span className="block text-sm font-medium text-gray-700">
            {t("documents.generate.render_type")}
          </span>
          <p className="mt-1 text-xs text-gray-500">
            {t("documents.generate.render_type_info")}
          </p>
          <div className="mt-2">
            <div className="flex items-center">
              <input
                id="document-type-dryrun"
                name="document-type"
                type="radio"
                checked={isDryRun}
                onChange={() => setIsDryRun(true)}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300"
              />
              <label
                htmlFor="document-type-dryrun"
                className="ml-2 block text-sm text-gray-700"
              >
                {t("documents.generate.dry_run")}
              </label>
            </div>
            <div className="flex items-center">
              <input
                id="document-type-official"
                name="document-type"
                type="radio"
                checked={!isDryRun}
                onChange={() => setIsDryRun(false)}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300"
              />
              <label
                htmlFor="document-type-official"
                className="ml-2 block text-sm text-gray-700"
              >
                {t("documents.generate.official_render")}
              </label>
            </div>
          </div>
        </div>

        {!isDryRun && (
          <>
            <div>
              <label
                htmlFor="revision-name"
                className="block text-sm font-medium text-gray-700"
              >
                {t("documents.generate.revision_name")}
              </label>
              <input
                type="text"
                id="revision-name"
                value={revisionName}
                onChange={(e) => {
                  const newValue = e.target.value;
                  // Check if the input contains only allowed characters
                  if (/^[a-zA-Z0-9_\-:.]*$/.test(newValue)) {
                    setRevisionName(newValue);
                    setRevisionNameError("");
                  } else {
                    // Set error message but don't update the input value
                    setRevisionNameError(
                      t("documents.generate.revision_name_error")
                    );
                  }
                }}
                className={`mt-1 block w-full border ${
                  revisionNameError ? "border-red-500" : "border-gray-300"
                } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm`}
                placeholder="v1.0"
              />
              <p className="mt-1 text-xs text-gray-500">
                {t("documents.generate.revision_name_info")}
              </p>
              {revisionNameError && (
                <p className="mt-1 text-xs text-red-500">{revisionNameError}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="comment"
                className="block text-sm font-medium text-gray-700"
              >
                {t("documents.generate.comment")}
              </label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                placeholder={t("documents.generate.comment_placeholder")}
              />
            </div>
            {isFeatureEnabled(Feature.OnTheFlyTranslations) && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  {t("documents.generate.selected_languages")}
                </h4>
                <div className="bg-gray-50 rounded-md p-3 text-sm">
                  {availableLanguages.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {availableLanguages.map((lang) => (
                        <div key={lang} className="flex items-center">
                          <input
                            id={`lang-${lang}`}
                            type="checkbox"
                            className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                            checked={userSelectedLanguages.includes(lang)}
                            onChange={() => handleLanguageToggle(lang)}
                          />
                          <label
                            htmlFor={`lang-${lang}`}
                            className="ml-2 block text-sm text-gray-700 flex items-center"
                          >
                            <span className="pr-2">
                              <img
                                src={`https://flagcdn.com/16x12/${flagsCode[lang]}.png`}
                                srcSet={`https://flagcdn.com/32x24/${flagsCode[lang]}.png 2x,
    https://flagcdn.com/48x36/${flagsCode[lang]}.png 3x`}
                                width="16"
                                height="12"
                                alt="Ukraine"
                              ></img>
                            </span>
                            {t(`languages.${lang}`)}
                          </label>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="italic text-gray-500">
                      {t("documents.generate.no_languages_available")}
                    </p>
                  )}
                  {userSelectedLanguages.length === 0 && (
                    <p className="mt-2 text-sm text-red-500">
                      {t("documents.generate.no_languages_selected")}
                    </p>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div>
          <span className="text-sm font-medium text-gray-700 mb-2">
            {t("documents.generate.selected_documents")}
          </span>
          {selectedDocs.length > 0 && (
            <>
              {" "}
              <p className="text-gray-500 text-xs py-1">
                {t("documents.generate.selected_documents_info")}
              </p>
              <div className="bg-gray-50 rounded-md p-3 text-sm">
                {selectedDocs.length > 0 ? (
                  <ul className="list-disc pl-5">
                    {selectedDocs.map((doc) => (
                      <li key={doc}>{t(`documents.${doc}.label`)}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="italic text-gray-500">
                    {t("documents.generate.no_documents_selected")}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Project changes diff */}
        {/* <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            {t("documents.generate.json_diff")}
          </h4>
          {project && previousRevisionKey ? (
            <JsonDiff
              currentJson={currentProject}
              previousJson={previousSnapshot}
              changedKeys={changedKeys}
              isLoading={isDiffLoading}
              error={diffError || undefined}
            />
          ) : (
            <div className="bg-gray-50 rounded-md p-3 text-sm h-40 flex items-center justify-center">
              <p className="italic text-gray-500">
                {t("documents.generate.no_previous_revision")}
              </p>
            </div>
          )} 
        </div>*/}

        {!isDryRun && (
          <p className="mt-1 text-xs text-gray-500">
            {t("documents.generate.revision_info")}
          </p>
        )}
      </div>

      <div className="flex justify-end items-center space-x-3">
        <a
          className="px-6 text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
          onClick={onClose}
        >
          {t("form.cta.cancel")}
        </a>
        <PrimaryButton
          onClick={handleSubmit}
          disabled={userSelectedLanguages.length === 0}
        >
          {t("documents.generate.submit")}
        </PrimaryButton>
      </div>
    </div>
  );

  return (
    <Modal show={isOpen} onClose={onClose} content={modalContent} width="1/2" />
  );
};
